<template>
  <v-layout column>
    <vbt-content-box
      :loading="isLoading"
      :loading-error="loadingError"
      :no-content="noContent"
      title="Information"
    >
      <template #toolbar>
        <v-layout
          align-center
          justify-end
        >
          <v-flex lg3>
            <v-layout align-center>
              <strong class="body-1 mr-2">
                Show only active
              </strong>
              <v-switch
                v-model="activeOnly"
                color="success"
                class="vbt-hide-form-control-details"
              />
            </v-layout>
          </v-flex>
          <v-flex
            class="pl-2"
            lg3
          >
            <ums-applications-count :apps="applications" />
          </v-flex>
          <v-btn
            small
            class="ml-4"
            color="success"
            @click="openEditorDialog()"
          >
            Create app
          </v-btn>
        </v-layout>
      </template>
      <vbt-table
        :headers="headers"
        :items="applications"
        :page-size="15"
        searchable
        show-paginator
        show-edit-action
        @click:edit="openEditorDialog"
      >
        <template #name="{ item: { name, relatedUserUniqId } }">
          <router-link :to="{
            name: 'user',
            params: { id: relatedUserUniqId },
          }">
            {{ name }}
          </router-link>
        </template>
        <template #usersCount="{ item }">
          <v-btn
            small
            outlined
            color="info"
            @click="openAppUsersDialog(item)"
          >
            Show users ({{ item.usersCount }})
          </v-btn>
        </template>
        <template #roles="{ item }">
          <v-btn
            small
            outlined
            color="info"
            @click="openAppRolesDialog(item)"
          >
            Show roles ({{ (item.roles || []).length }})
          </v-btn>
        </template>
        <template #state="{ item: { state } }">
          <ums-status-badge :status="state">
            {{ state ? 'Enabled' : 'Disabled' }}
          </ums-status-badge>
        </template>
        <template #prepend-actions="{ item }">
          <v-layout align-center>
            <ums-application-state-switcher
              :name="item.name"
              :state="item.state"
              @success="$_fetchData"
            />
            <ums-password-change-activator
              :disabled="!item.state"
              :updatable-name="item.name"
              @click="openPasswordChangerDialog(item)"
            />
            <v-tooltip left>
              <template #activator="{ on }">
                <v-btn
                  icon
                  small
                  color="warning"
                  v-on="on"
                  @click="openAppLongTimeTokenDialog(item)"
                >
                  <v-icon>
                    schedule
                  </v-icon>
                </v-btn>
              </template>
              Generate longtime token
            </v-tooltip>
          </v-layout>
        </template>
        <template #append-actions="{ item }">
          <v-layout align-center>
            <v-tooltip left>
              <template #activator="{ on }">
                <v-btn
                  :to="{ name: 'applicationHistory', params: { app: item.name } }"
                  color="info"
                  icon
                  v-on="on"
                >
                  <v-icon>
                    book
                  </v-icon>
                </v-btn>
              </template>
              History
            </v-tooltip>
          </v-layout>
        </template>
      </vbt-table>
    </vbt-content-box>

    <ums-application-editor
      v-model="editorDialog"
      :creator="isEditorCreateMode"
      :item="editableApp"
      @success="$_fetchData"
    />

    <ums-application-password-changer
      v-model="passwordChangerDialog"
      :app="editableApp.name"
      @success="$_fetchData"
    />

    <ums-application-roles
      v-model="appRolesDialog"
      :app="editableApp.name"
      @success="$_fetchData"
    />

    <ums-application-users
      v-model="appUsersDialog"
      :app="editableApp.name"
      :roles="editableApp.roles"
      @success="$_fetchData"
    />

    <ums-application-long-time-token-generator
      v-model="appLongTimeTokenDialog"
      :app="editableApp.name"
      :roles="editableApp.roles"
    />
  </v-layout>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import { useEditorWithDialog, useDataLoading } from '@mixins/factories';
import { handlerActiveOnly } from '@mixins';

import {
  namespace as LocationsNamespace,
  ActionTypes as LocationsActionTypes,
} from '@modules/locations/store.types';

import {
  namespace as ApplicationsNamespace,
  ActionTypes as ApplicationsActionTypes,
} from '../store.type';

import UmsApplicationRoles from '../_components/UmsApplicationRoles';
import UmsApplicationUsers from '../_components/UmsApplicationUsers';
import UmsApplicationsCount from '../_components/UmsApplicationsCount.vue';
import UmsApplicationEditor from '../_components/UmsApplicationEditor.vue';
import UmsApplicationStateSwitcher from '../_components/UmsApplicationStateSwitcher.vue';
import UmsApplicationPasswordChanger from '../_components/UmsApplicationPasswordChanger.vue';
import UmsApplicationLongTimeTokenGenerator from '../_components/UmsApplicationLongTimeTokenGenerator.vue';

const dataLoadingOptions = {
  getterName: ApplicationsActionTypes.GET_APPLICATIONS,
  specialQueryParams: [{ type: 'booleanString', name: 'activeOnly' }],
  contentName: 'applications',
  searchable: false,
  paged: false,
};

const headers = Object.freeze([
  { text: 'Name', value: 'name', sortable: true },
  { text: 'Description', value: 'description' },
  { text: 'Users', value: 'usersCount' },
  { text: 'Roles', value: 'roles' },
  { text: 'Token Lifetime', value: 'tokenLifetime' },
  { text: 'State', value: 'state' },
]);

export default {
  name: 'UmsApplications',
  components: {
    UmsApplicationRoles,
    UmsApplicationUsers,
    UmsApplicationsCount,
    UmsApplicationEditor,
    UmsApplicationStateSwitcher,
    UmsApplicationPasswordChanger,
    UmsApplicationLongTimeTokenGenerator,
  },
  mixins: [
    useEditorWithDialog({ editableItemName: 'editableApp' }),
    useEditorWithDialog({
      editorDialogFlagName: 'passwordChangerDialog',
      openTriggerName: 'openPasswordChangerDialog',
      editableItemName: 'editableApp',
    }),
    useEditorWithDialog({
      editorDialogFlagName: 'appRolesDialog',
      openTriggerName: 'openAppRolesDialog',
      editableItemName: 'editableApp',
    }),
    useEditorWithDialog({
      editorDialogFlagName: 'appUsersDialog',
      openTriggerName: 'openAppUsersDialog',
      editableItemName: 'editableApp',
    }),
    useEditorWithDialog({
      editorDialogFlagName: 'appLongTimeTokenDialog',
      openTriggerName: 'openAppLongTimeTokenDialog',
      editableItemName: 'editableApp',
    }),
    useDataLoading(dataLoadingOptions),
    handlerActiveOnly,
  ],
  data: () => ({ headers }),
  computed: {
    ...mapState(ApplicationsNamespace, ['applications']),
  },
  created() {
    this.getLocations();
  },
  methods: {
    ...mapActions(ApplicationsNamespace, [ApplicationsActionTypes.GET_APPLICATIONS]),
    ...mapActions(LocationsNamespace, {
      getLocations: LocationsActionTypes.GET_LOCATIONS,
    }),
  },
};
</script>
